/**
 * Display metadata for entity metrics on the Social Analytics Dashboard.
 *
 * **The client no longer carries a metric catalog.** Until EN-T20 this module
 * owned `PIPELINE_METRICS` — the hard-coded list of pipeline label keys, their
 * display names, their aggregation and their percent-vs-number format — and
 * `metrics-aggregator.ts` re-derived every KPI in the browser from ≤5000
 * pipeline label strings.
 *
 * Both are gone. The numbers now come from `POST /entities/metrics/query`
 * (entities plan part 6 §2), whose per-metric objects carry `displayName`,
 * `agg` and `format` alongside the value, so the server catalog
 * (`goapi/pkg/autocoder/entitymetrics/catalog.go`) is the single source of
 * truth for both the maths and the rendering.
 *
 * What survives here is only what a *renderer* needs: the metric keys the KPI
 * strip asks for, and the number→string formatting rules.
 *
 * `MetricsCards` (./MetricsCards.tsx) is no longer pinned to the two KPI-strip
 * keys: it takes an optional `keys` prop naming any catalog metric keys, for
 * callers such as a customer grid cell that wants its own subset. A key with
 * no data is dropped by default (unchanged), or — with `renderEmpty` — drawn
 * as a "—" placeholder tile instead of vanishing, so a grid cell keeps a
 * stable shape across rows with different data.
 */

export type AggregationType = 'sum' | 'avg'
export type FormatType = 'number' | 'percent'

/**
 * One metric's number in one group, as returned by
 * `POST /entities/metrics/query` (server `EntityMetricValue`).
 *
 * `format` is load-bearing: it is now the ONLY carrier of percent-vs-number
 * rendering — a rate arriving without it would silently render `0.368` where
 * the page has always shown `0.4%`.
 */
export interface EntityMetricValue {
  value: number
  /** Effective aggregation — the request override, else the catalog default. */
  agg: AggregationType
  /** Entities contributing THIS metric (not the group's entity total). */
  entityCount: number
  displayName: string
  format: FormatType
}

/** A metric value plus the catalog key it was requested under. */
export interface KeyedEntityMetricValue extends EntityMetricValue {
  key: string
}

/**
 * The entity metric keys the KPI strip requests in its single
 * `/entities/metrics/query` call. These are catalog keys (part 2 §3), NOT the
 * legacy pipeline label keys — e.g. `u35_pct`, previously `u35_percent`.
 *
 * The list is the full v1 catalog rather than only the two rendered tiles: it
 * costs one scan either way, and it keeps the response a complete picture of
 * the filtered population for the parity harness (EN-T21) and for tiles added
 * later.
 */
export const KPI_METRIC_KEYS: string[] = [
  'video_reach',
  'video_views',
  'engagements',
  'post_comments',
  'post_shares',
  'post_likes',
  'engagement_rate_pct',
  'meaningful_er_pct',
  'meaningful_engagement_score',
  'u35_pct',
]

/**
 * Format a metric value for display.
 *
 * Percent metrics are stored as they are displayed (0.368 = 0.368%), so
 * `percent` appends a suffix and never multiplies by 100.
 */
export function formatMetricValue(value: number | null | undefined, format: FormatType): string {
  if (value === null || value === undefined) return '—'

  if (format === 'percent') {
    return `${value.toFixed(1)}%`
  }

  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`
  }

  return value.toLocaleString('en-US', {
    maximumFractionDigits: 1,
  })
}

/** Human label for the aggregation, used in the tile subtitle/tooltip. */
export function aggregationLabel(agg: AggregationType): string {
  return agg === 'sum' ? 'Total' : 'Avg'
}
